import { createActionTypes } from 'apps/new-web/lib/redux-utils';
import { SubmissionError } from 'redux-form';
import analytics from 'apps/new-web/lib/analytics';
import request from 'apps/new-web/lib/request';
import winston from 'apps/new-web/lib/winston';
import I18n from 'apps/new-web/views/components/i18n';

const errors = {
  ACCOUNT_LOCKED: I18n.translate('This account is locked. Please reset your password to unlock it.'),
  ACCOUNT_WAS_LOCKED: I18n.translate('Too many invalid login attempts, your account has been locked. You may reset your password to unlock it.'),
  ACCOUNT_LOCKED_MINUTE: I18n.translate('Too many invalid login attempts, your account has been locked for 1 minute.'),
  accountLockedMinutes: v => I18n.translate('Too many invalid login attempts, your account has been locked for [minutes] minutes.', { minutes: v }),
  ACCOUNT_LOCKED_HOUR: I18n.translate('Too many invalid login attempts, your account has been locked for 1 hour.'),
  accountLockedHours: v => I18n.translate('Too many invalid login attempts, your account has been locked for [hours] hours.', { hours: v }),
  ACCOUNT_LOCKED_DAY: I18n.translate('Too many invalid login attempts, your account has been locked for 1 day.'),
  ACCOUNT_LOCKED_MONTH: I18n.translate('Too many invalid login attempts, your account has been locked for 1 month.'),
  ACCOUNT_LOCKED_YEAR: I18n.translate('Too many invalid login attempts, your account has been locked for 1 year.'),
  LOGIN_SUBMISSION_INVALID: I18n.translate('Email or password is invalid.'),
  NO_FACEBOOK_USER: I18n.translate('No user is associated with your Facebook account.'),
  NO_GOOGLE_USER: I18n.translate('No user is associated with your Google account.'),
  SERVER_ERROR: I18n.translate('Looks like there was a problem logging you in. Please try again.')
};

export const actionTypes = createActionTypes('LOGIN_PAGE', ['SET_REMEMBER_ME', 'SET_THIRD_PARTY_LOGIN_ERROR', 'SET_USER_INFO']);

export const setRememberMe = rememberMe => ({
  type: actionTypes.SET_REMEMBER_ME,
  rememberMe
});

export const setThirdPartyLoginError = error => ({
  type: actionTypes.SET_THIRD_PARTY_LOGIN_ERROR,
  error
});

export const setUserInfo = userInfo => ({
  type: actionTypes.SET_USER_INFO,
  userInfo
});

export const login = ({ email, password, rememberMe }) => (dispatch, getState) => {
  const format = args => Object.keys(args).map(arg => `${arg}=${args[arg]}`).join(' ');
  const { isZendeskSSO, next, rememberMe } = getState();
  winston.info('Website login action request. ' + format({ email, userAgent: navigator.userAgent }));
  return request.post('/login')
    .send({ email, password, rememberMe })
    .end()
    .then(() => {
      analytics.trackSignedIn({ email });
      setBranchIdentity();
      window.location = isZendeskSSO ? next : getPostLoginPath(next);
    })
    .catch((err) => {
      winston.info('Website login action response error. ' + format({ email, error: err.message, status: err.status }));
      switch (err.status) {
        case 400: // email or pw formatted incorrectly
        case 401:
          const {
            accountLocked,
            accountLockoutApplied
          } = err.response.body || {};

          if (accountLockoutApplied) {
            const { unit, value } = accountLockoutApplied;

            switch (unit) {
              case 'minute': throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED_MINUTE });
              case 'minutes': throw new SubmissionError({ _error: errors.accountLockedMinutes(value) });
              case 'hour': throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED_HOUR });
              case 'hours': throw new SubmissionError({ _error: errors.accountLockedHours(value) });
              case 'day': throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED_DAY });
              case 'month': throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED_MONTH });
              case 'year': throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED_YEAR });
              default: throw new SubmissionError({ _error: errors.ACCOUNT_WAS_LOCKED });
            }
          }

          if (accountLocked) throw new SubmissionError({ _error: errors.ACCOUNT_LOCKED });
          throw new SubmissionError({ _error: errors.LOGIN_SUBMISSION_INVALID });
        default: throw new SubmissionError({ _error: errors.SERVER_ERROR });
      }
    });
};

export const loginWithFacebook = token => (dispatch, getState) => {
  const { rememberMe } = getState();
  return request.post('/login/facebook')
    .send({ token, rememberMe })
    .end()
    .then(() => {
      setBranchIdentity();
      window.location = getPostLoginPath();
    })
    .catch((err) => {
      switch (err.status) {
        case 404: return dispatch(setThirdPartyLoginError(errors.NO_FACEBOOK_USER));
        default: return dispatch(setThirdPartyLoginError(errors.SERVER_ERROR));
      }
    });
};

export const loginWithGoogle = token => (dispatch, getState) => {
  const { rememberMe } = getState();
  return request.post('/login/google')
    .send({ token, rememberMe })
    .end()
    .then(() => {
      setBranchIdentity();
      window.location = getPostLoginPath();
    })
    .catch((err) => {
      switch (err.status) {
        case 404: return dispatch(setThirdPartyLoginError(errors.NO_GOOGLE_USER));
        default: return dispatch(setThirdPartyLoginError(errors.SERVER_ERROR));
      }
    });
};

const setBranchIdentity = () => {
  fetch('web-api/verify-session')
    .then(res => res.json())
    .then((data) => {
      if (data.id) {
        window.branch.setIdentity(data.id);
      }
    })
    .catch();
};

function getPostLoginPath(next) {
  let url = '/complete-profile';
  if (next) {
    url += `?next=${encodeURIComponent(next)}`;
  }
  return url;
}

export const actionCreators = {
  login,
  loginWithFacebook,
  loginWithGoogle,
  setRememberMe,
  setThirdPartyLoginError,
  setUserInfo
};
